import * as R from "ramda";
import * as React from "react";
import * as Autosuggest from "react-autosuggest";
import { FaPlus } from "react-icons/fa";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { IAction } from "reduxsauce";
import styled from "styled-components";
import { AxiosInterceptedResult } from "../../../../api/interceptors";
import { IUser } from "../../../../App/Analytics/Common";
import { AddButton, ButtonRow, CustomSearchInput, SearchField } from "../../../../App/Common/Components/FormComponents";
import { SectionTitle } from "../../../../App/Common/Styles/containers";
import UserRow from "../../../../App/Common/Styles/UserLink";
import { IState } from "../../../../App/reducer";
import { ResolveRequestResponse } from "../../../../reducers/course-author/types";
import { Creators } from "../../../../sagas/course-author/watchers";
import {
  deleteElementPatch,
  insertElementPatch,
  patchPayloadCreator,
} from "../../../course-author/helpers/patches/creators";
import { ICourseResolveDisplayResponse, IFullCourse } from "../../../course-author/types";
import CustomModal, { ModalType } from "../../../utils/CustomModal";
import AutoComplete from "../Autocomplete";

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export interface IAssignEditorsStateProps {
  currentOrgId: string;
  eligibleEditors: Array<[string, ICourseResolveDisplayResponse]>;
  getEligibleEditors: (searchValue?: string) => Promise<AxiosInterceptedResult<string[]>>;
  isOrgAdmin: boolean;
}

export interface IAssignEditorsDispatchProps {
  addEditorToCourse: (id: string) => void;
  removeEditor: (id: string) => () => void;
  resolve: (key: string, data: string[]) => void;
}

export interface IAssignEditorsOwnProps {
  course: IFullCourse;
  courseId: string;
  editors: Array<ResolveRequestResponse<ICourseResolveDisplayResponse>>;
  userId: string;
}

export interface IAssignEditorsState {
  selected: string | null;
  addUser: string;
  isModalVisible: boolean;
}

type IAssignEditorsProps = IAssignEditorsStateProps & IAssignEditorsDispatchProps & IAssignEditorsOwnProps;

const chooseUser = "choose-user";
export class AssignEditors extends React.Component<IAssignEditorsProps, IAssignEditorsState> {
  private removeEditorAlertMsg = (
    <>
      You cannot remove yourself as an Editor, please contact an Administrator or{" "}
      <a href="support@saltcompliance.com">support@saltcompliance.com</a> for assistance.
    </>
  );

  constructor(props: IAssignEditorsProps) {
    super(props);
    this.state = {
      addUser: "",
      isModalVisible: false,
      selected: null,
    };
  }

  public addNewEditor = () => {
    if (R.isNil(this.state.selected)) {
      return;
    }

    this.props.addEditorToCourse(this.state.selected);
  };

  public onSuggestionsFetchRequested = ({ value }: Autosuggest.SuggestionsFetchRequestedParams) => {
    this.props.getEligibleEditors(value).then(([error, response]: AxiosInterceptedResult<string[]>) => {
      if (R.isNil(error) && !R.isNil(response)) {
        const data = R.map(id => `${id}?display`, R.slice<string>(0, 50, response.data));
        this.props.resolve("eligibleEditors", data);
      }
    });
  };

  public render() {
    const users: IUser[] = this.props.editors.map(([accountId, admin]) => ({
      accountId,
      displayName: admin.displayName,
    }));

    return (
      <>
        <SectionTitle>Editors</SectionTitle>
        <FormContainer>
          <SearchField legend={"Enter User or Group name"}>
            <AutoComplete
              fetchSuggestions={this.onSuggestionsFetchRequested}
              htmlName={chooseUser}
              items={this.props.eligibleEditors}
              itemValue={R.pathOr("", [1, "displayName"])}
              placeholderText="Type here to look for User or Group"
              renderInputComponent={CustomSearchInput}
              setSelected={this.selectNewEditor}
            />
          </SearchField>
          <ButtonRow width={15} margin={1}>
            <AddButton onClick={this.addNewEditor}>
              <span>
                <FaPlus /> Add
              </span>
            </AddButton>
          </ButtonRow>
        </FormContainer>
        {users.map(this.renderUserRow)}
        <CustomModal
          type={ModalType.Warning}
          showModal={this.state.isModalVisible}
          closeModal={this.toggleModal}
          message={this.removeEditorAlertMsg}
        />
      </>
    );
  }

  private toggleModal = () => this.setState({ isModalVisible: !this.state.isModalVisible });

  private removeEditorOrNotifyInvalidAction = (id: string) => () => {
    if (id.includes(this.props.userId) && !this.props.isOrgAdmin) {
      return this.toggleModal();
    } else {
      return this.props.removeEditor(id);
    }
  };

  private renderUserRow: (user: IUser) => JSX.Element = user => (
    <UserRow key={user.accountId} user={user} removeUser={this.removeEditorOrNotifyInvalidAction} />
  );

  private selectNewEditor = (suggestion: [string, ICourseResolveDisplayResponse]) =>
    this.setState({ selected: suggestion[0] });
}

const stripSuffix = R.slice(0, 40);

export default connect<IAssignEditorsStateProps, IAssignEditorsDispatchProps, IAssignEditorsOwnProps, IState>(
  (state: IState, ownProps: IAssignEditorsOwnProps) => ({
    currentOrgId: state.App.CourseAuthor._.course!.state.owner,
    eligibleEditors: state.App.CourseAuthor._.resolvedData.eligibleEditors,
    getEligibleEditors: state.App.CourseAuthor._.autocompleteApi.getEligibleEditors,
    isOrgAdmin: state.App._.user!.isOrgAdmin,
  }),
  (dispatch: Dispatch<IAction>, ownProps: IAssignEditorsOwnProps) => ({
    addEditorToCourse: (id: string) =>
      dispatch(
        Creators.patchCourse(
          patchPayloadCreator(
            ownProps.course.versionId,
            insertElementPatch(ownProps.course, ownProps.userId, stripSuffix(id), "admins")
          )
        )
      ),
    removeEditor: (id: string) =>
      dispatch(
        Creators.patchCourse(
          patchPayloadCreator(
            ownProps.course.versionId,
            deleteElementPatch(ownProps.course, ownProps.userId, stripSuffix(id), "admins")
          )
        )
      ),
    resolve: (key: string, requestData: string[]) => dispatch(Creators.resolveAuxiliaryData(key, requestData)),
  })
)(AssignEditors);
